<script setup lang="ts">
import { computed, type Component } from 'vue'
import { Palette, type PaletteHue, type PaletteName } from '../palette'

/**
 * EntityIcon — coloured icon container with semantic colour treatment.
 *
 * Mirrors core/ui/components/EntityIcon.kt:
 *   .soft fill + .ink icon (NOT .solid — that's the picker swatch)
 *   14px radius (lg) to match Android cards
 *
 * Pass a hue three ways:
 *   1. <EntityIcon :icon="Users" hue="teal" />      (palette name)
 *   2. <EntityIcon :icon="Users" :palette="Palette.teal" />
 *   3. <EntityIcon :icon="Users" color="#14A99C" /> (legacy raw hex)
 *
 * The raw-hex form derives soft/ink from the hex (15% alpha fill, hex icon).
 * Prefer hue/palette so colour stays in the design system.
 *
 *   Entity      | Lucide icon  | Hue source             | Default size
 *   ------------|--------------|------------------------|-------------
 *   Class       | Users        | hash(classId)          | 48
 *   Course      | BookOpen     | hash(courseId)         | 48
 *   Student     | UserRound    | hash(studentId) or grey| 48
 */

const props = withDefaults(defineProps<{
  /** Lucide icon component */
  icon: Component
  /** Palette name (preferred) — e.g. 'teal' */
  hue?: PaletteName
  /** Full hue object (also fine) */
  palette?: PaletteHue
  /** Raw hex — legacy. soft/ink derived from this. */
  color?: string
  /** Container size in px */
  size?: number
  /** Icon size in px (defaults to ~55% of container) */
  iconSize?: number
  /** Corner radius in px — defaults to radius-lg (14) */
  cornerRadius?: number
  /** Background opacity when passing a raw `color` hex */
  backgroundAlpha?: number
  /** Optional border on the container */
  borderWidth?: number
  borderAlpha?: number
}>(), {
  size: 48,
  cornerRadius: 14,
  backgroundAlpha: 0.15,
  borderWidth: 0,
  borderAlpha: 0.3,
})

const computedIconSize = computed(() => props.iconSize ?? Math.round(props.size * 0.55))

const resolvedHue = computed<PaletteHue | null>(() => {
  if (props.palette) return props.palette
  if (props.hue) return Palette[props.hue]
  return null
})

function hexToRgb(hex: string): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

const fillStyle = computed(() => {
  const hue = resolvedHue.value
  if (hue) {
    return { background: hue.soft, color: hue.ink }
  }
  const fallback = props.color ?? '#71717A'
  const rgb = hexToRgb(fallback)
  return {
    background: `rgba(${rgb}, ${props.backgroundAlpha})`,
    color: fallback,
  }
})

const containerStyle = computed(() => {
  const fill = fillStyle.value
  const style: Record<string, string> = {
    width: `${props.size}px`,
    height: `${props.size}px`,
    borderRadius: `${props.cornerRadius}px`,
    backgroundColor: fill.background,
    color: fill.color,
  }
  if (props.borderWidth > 0) {
    const borderColour = resolvedHue.value?.ink ?? props.color ?? '#71717A'
    const rgb = hexToRgb(borderColour)
    style.border = `${props.borderWidth}px solid rgba(${rgb}, ${props.borderAlpha})`
  }
  return style
})

const iconColor = computed(() => fillStyle.value.color)
</script>

<template>
  <div
    class="inline-flex items-center justify-center flex-shrink-0"
    :style="containerStyle"
  >
    <component
      :is="icon"
      :size="computedIconSize"
      :color="iconColor"
      :stroke-width="2"
    />
  </div>
</template>
